import React, { useEffect, useState } from "react";
import PageHeader from "../../../common/pageheader/pageheader";
import AGTable from "../../../common/table/AGTable";
import api from "../../../../resources/api";
import Loader from "../../../common/loader/loader";
import { currencyConverter, formatDateAndTime } from "../../../../resources/constants";
import { connect } from "react-redux";

const today = () => new Date().toISOString().split("T")[0];
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split("T")[0]; };

function DailyCollection() {
    const [isLoading, setIsLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summaryStats, setSummaryStats] = useState({
        TotalCollected: 0, CardTotal: 0, BankTransferTotal: 0,
        WalletTotal: 0, MixedTotal: 0, TransactionCount: 0,
    });
    const [filters, setFilters] = useState({ from_date: daysAgo(30), to_date: today() });
    const [datatable, setDatatable] = useState({
        columns: [
            { label: "Date", field: "Date" },
            { label: "Transactions", field: "TransactionCount" },
            { label: "Total Collected", field: "TotalCollected" },
            { label: "Card", field: "CardTotal" },
            { label: "Bank Transfer", field: "BankTransferTotal" },
            { label: "Wallet", field: "WalletTotal" },
            { label: "Mixed", field: "MixedTotal" },
        ],
        rows: [],
    });

    const getReportData = async () => {
        if (!filters.from_date || !filters.to_date) return;
        setIsLoading(true);
        const result = await api.get(`staff/finance/invoices/daily-collection?from_date=${filters.from_date}&to_date=${filters.to_date}`);
        if (result.success && result.data?.data) {
            const { summary, rows } = result.data.data;
            setSummaryStats(summary || {});
            setReportData(rows || []);
            buildTable(rows || []);
        }
        setIsLoading(false);
    };

    const buildTable = (data) => {
        const rows = data.map((item) => ({
            Date: <span className="fw-bold">{formatDateAndTime(item.Date, "date")}</span>,
            TransactionCount: <span className="badge badge-light-primary">{item.TransactionCount}</span>,
            TotalCollected: <span className="text-success fw-bold">{currencyConverter(item.TotalCollected)}</span>,
            CardTotal: <span>{currencyConverter(item.CardTotal)}</span>,
            BankTransferTotal: <span>{currencyConverter(item.BankTransferTotal)}</span>,
            WalletTotal: <span className="text-info">{currencyConverter(item.WalletTotal)}</span>,
            MixedTotal: <span>{currencyConverter(item.MixedTotal)}</span>,
        }));
        setDatatable(prev => ({ ...prev, rows }));
    };

    const exportToCSV = () => {
        if (!reportData.length) return;
        const headers = ["Date", "Transactions", "Total Collected", "Card", "Bank Transfer", "Wallet", "Mixed"];
        const rows = reportData.map(item => [
            formatDateAndTime(item.Date, "date"), item.TransactionCount, item.TotalCollected,
            item.CardTotal, item.BankTransferTotal, item.WalletTotal, item.MixedTotal,
        ]);
        const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(",")).join("\n");
        const a = document.createElement("a");
        a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        a.download = `daily_collection_${filters.from_date}_to_${filters.to_date}.csv`;
        a.click();
    };

    const onFilterChange = (e) => setFilters(f => ({ ...f, [e.target.id]: e.target.value }));

    const channelBoxes = [
        { label: "Card", value: summaryStats.CardTotal, color: "primary" },
        { label: "Bank Transfer", value: summaryStats.BankTransferTotal, color: "success" },
        { label: "Wallet", value: summaryStats.WalletTotal, color: "info" },
        { label: "Mixed", value: summaryStats.MixedTotal, color: "warning" },
    ];

    return isLoading ? <Loader /> : (
        <div className="d-flex flex-column flex-row-fluid">
            <PageHeader title="Daily Collection Summary" items={["Finance", "Invoices", "Daily Collection"]} />
            <div className="flex-column-fluid">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label required">From Date</label>
                                <input type="date" id="from_date" className="form-control" value={filters.from_date} onChange={onFilterChange} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label required">To Date</label>
                                <input type="date" id="to_date" className="form-control" value={filters.to_date} onChange={onFilterChange} />
                            </div>
                            <div className="col-md-4 d-flex gap-2 justify-content-end">
                                <button className="btn btn-primary" onClick={getReportData}>
                                    <i className="fa fa-search me-2"></i>Generate
                                </button>
                                <button className="btn btn-light-primary" onClick={exportToCSV} disabled={!reportData.length}>
                                    <i className="fa fa-download me-2"></i>CSV
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {reportData.length > 0 && (
                    <>
                        <div className="row g-4 mb-4">
                            <div className="col-md-3">
                                <div className="card bg-light-success">
                                    <div className="card-body py-4">
                                        <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px me-3">
                                                <span className="symbol-label bg-success"><i className="fa fa-money-bill-wave text-white fs-3"></i></span>
                                            </div>
                                            <div>
                                                <div className="fs-5 fw-bold">{currencyConverter(summaryStats.TotalCollected || 0)}</div>
                                                <div className="text-muted small">Total Collected</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-light-primary">
                                    <div className="card-body py-4">
                                        <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px me-3">
                                                <span className="symbol-label bg-primary"><i className="fa fa-receipt text-white fs-3"></i></span>
                                            </div>
                                            <div>
                                                <div className="fs-2 fw-bold">{summaryStats.TransactionCount || 0}</div>
                                                <div className="text-muted small">Total Transactions</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-light-info">
                                    <div className="card-body py-4">
                                        <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px me-3">
                                                <span className="symbol-label bg-info"><i className="fa fa-credit-card text-white fs-3"></i></span>
                                            </div>
                                            <div>
                                                <div className="fs-5 fw-bold">{currencyConverter((summaryStats.CardTotal || 0) + (summaryStats.BankTransferTotal || 0))}</div>
                                                <div className="text-muted small">Gateway Total</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-light-warning">
                                    <div className="card-body py-4">
                                        <div className="d-flex align-items-center">
                                            <div className="symbol symbol-50px me-3">
                                                <span className="symbol-label bg-warning"><i className="fa fa-wallet text-white fs-3"></i></span>
                                            </div>
                                            <div>
                                                <div className="fs-5 fw-bold">{currencyConverter(summaryStats.WalletTotal || 0)}</div>
                                                <div className="text-muted small">Wallet Total</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card mb-4">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Channel Breakdown</h3>
                            </div>
                            <div className="card-body py-3">
                                <div className="d-flex flex-wrap gap-4 justify-content-center">
                                    {channelBoxes.map((b, i) => (
                                        <div key={i} className="border rounded p-4 text-center" style={{ minWidth: 160 }}>
                                            <div className={`fs-4 fw-bold text-${b.color}`}>{currencyConverter(b.value || 0)}</div>
                                            <div className="text-muted small mt-1">{b.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header border-0 pt-5">
                                <h3 className="card-title">Daily Breakdown</h3>
                                <div className="card-toolbar">
                                    <span className="text-muted fs-7">{reportData.length} days</span>
                                </div>
                            </div>
                            <div className="card-body py-3">
                                <AGTable data={datatable} />
                            </div>
                        </div>
                    </>
                )}

                {!reportData.length && !isLoading && (
                    <div className="card">
                        <div className="card-body text-center py-15">
                            <i className="fa fa-calendar-alt fs-2x text-muted mb-4"></i>
                            <p className="text-muted fs-5">Select a date range and click Generate to view daily collection</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default connect()(DailyCollection);
